import React, { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import { db, auth } from '../firebase';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, where } from 'firebase/firestore';

interface Comment {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  timestamp: any;
}

interface Props {
  incidentId: string;
}

export default function IncidentComments({ incidentId }: Props) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'incident_comments'),
      where('incidentId', '==', incidentId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const comms = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Comment));
      setComments(comms);
    });

    return () => unsubscribe();
  }, [incidentId]);

  const addComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !auth.currentUser) return;

    await addDoc(collection(db, 'incident_comments'), {
      incidentId,
      text: newComment,
      senderId: auth.currentUser.uid,
      senderName: auth.currentUser.displayName || 'Anonymous',
      timestamp: serverTimestamp()
    });
    setNewComment('');
  };

  return (
    <div className="mt-4 border-t border-amber-900/30 pt-4 font-mono">
      <h3 className="text-sm font-bold text-amber-500 mb-2 uppercase tracking-widest">Community Intel</h3>
      <div className="max-h-40 overflow-y-auto space-y-2 mb-2">
        {comments.map(comment => (
          <div key={comment.id} className="bg-black p-2 rounded-sm text-xs text-amber-600 border border-amber-900/30">
            <span className="font-bold text-amber-500">{comment.senderName}: </span>
            {comment.text}
          </div>
        ))}
      </div>
      <form onSubmit={addComment} className="flex gap-2">
        <input 
          type="text" 
          value={newComment} 
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add intel..."
          className="flex-1 terminal-input p-2 rounded-sm text-xs"
        />
        <button type="submit" className="bg-amber-600 text-black p-2 rounded-sm hover:bg-amber-500 transition-colors">
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}
